import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ordersApi, clientsApi } from "@/lib/api";
import { getOrderListStatus } from "@/lib/orderStatus";

export const useOrderMetrics = () => {
  const { data: allOrders, isLoading: isLoadingOrders } = useQuery({
    queryKey: ["allOrdersForMetrics"],
    queryFn: async () => {
      const response = await ordersApi.getOrderHistory();
      return response.data || [];
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true,
  });

  // Fonte oficial de clientes cadastrados — independente de já terem pedido no histórico.
  const { data: allClients, isLoading: isLoadingClients } = useQuery({
    queryKey: ["clientsForOrderFilter"],
    queryFn: async () => {
      const response = await clientsApi.getAll();
      return response.data || [];
    },
  });

  const metrics = useMemo(() => {
    const initial = {
      created: 0,
      pending: 0,
      processing: 0,
      canceled: 0,
      delivered: 0,
      enRoute: 0,
    };

    if (!allOrders) return initial;

    return allOrders.reduce((acc, order) => {
      const status = getOrderListStatus(order);
      if (status === "Criado") acc.created++;
      if (status === "Pendente") acc.pending++;
      if (status === "Processado") acc.processing++;
      if (status === "Cancelado") acc.canceled++;
      if (status === "Entregue") acc.delivered++;
      if (status === "Em Rota") acc.enRoute++;
      return acc;
    }, initial);
  }, [allOrders]);

  const clientsForFilter = useMemo(() => {
    if (!allClients) return [];

    const clientMap = new Map<string, { name: string; clientCode: string }>();
    for (const client of allClients) {
      const clientCode = client.clientCodeCnx || client.codigo_cnx;
      if (!clientCode || clientMap.has(clientCode)) continue;
      const name = client.clientFantasyName || client.clientCompanyName || client.nome_cliente;
      if (!name) continue;
      clientMap.set(clientCode, { name, clientCode });
    }

    return Array.from(clientMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [allClients]);

  return {
    metrics,
    clientsForFilter,
    isLoadingMetrics: isLoadingOrders || isLoadingClients,
  };
};
